import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * SlideOverPanel — right-docked detail panel. Pencil qKgr0.
 * Backed by Radix Dialog (focus trap + scroll lock + escape to close).
 * Use for user detail, audit event detail, application detail — anywhere
 * you need rich info without leaving the list.
 */
export const SlideOver = DialogPrimitive.Root
export const SlideOverTrigger = DialogPrimitive.Trigger
export const SlideOverClose = DialogPrimitive.Close

export interface SlideOverContentProps
  extends Omit<React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>, 'title'> {
  width?: number
  title?: React.ReactNode
  description?: React.ReactNode
}

export const SlideOverContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SlideOverContentProps
>(({ className, width = 560, title, description, children, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay
      className={cn(
        'fixed inset-0 z-50 bg-ia-overlay backdrop-blur-sm',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      )}
    />
    <DialogPrimitive.Content
      ref={ref}
      style={{ width }}
      className={cn(
        'fixed right-0 top-0 bottom-0 z-50 flex flex-col',
        'bg-card border-l border-ia-border shadow-lg',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
        'focus:outline-none',
        className,
      )}
      {...props}
    >
      <header className="shrink-0 flex items-start justify-between gap-3 px-6 py-4 border-b border-ia-border">
        <div className="flex flex-col gap-1 min-w-0">
          {title && (
            <DialogPrimitive.Title className="text-heading-md text-ia-text truncate">
              {title}
            </DialogPrimitive.Title>
          )}
          {description && (
            <DialogPrimitive.Description className="text-body-sm text-ia-text-secondary">
              {description}
            </DialogPrimitive.Description>
          )}
        </div>
        <DialogPrimitive.Close
          aria-label="Close panel"
          className="rounded-pill h-8 w-8 flex items-center justify-center text-ia-text-tertiary transition-all hover:bg-[var(--color-ia-sidebar-active-glow)] hover:text-[var(--color-ia-sidebar-active-fg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring shrink-0"
        >
          <X size={16} strokeWidth={1.75} />
        </DialogPrimitive.Close>
      </header>
      <div className="flex-1 overflow-y-auto">{children}</div>
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
))
SlideOverContent.displayName = 'SlideOverContent'

export const SlideOverBody: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => <div className={cn('px-6 py-4', className)} {...props} />

export const SlideOverFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      'shrink-0 px-6 py-4 border-t border-ia-border flex items-center justify-end gap-2',
      className,
    )}
    {...props}
  />
)
